import React, { useCallback, useEffect, useState } from 'react';
import styles from 'styles/components/SubscriptionInvoiceConfirmationCard.module.sass'
import { useToast } from 'provider/ToastProvider';
import SubscriptionChangeResult from 'interface/SubscriptionChangeResult';
import AccountType from 'interface/AccountType';
import { changeSubscription, fetchUserSubscription, activateV2Subscription } from 'api/SubscriptionApiService';
import { Elements } from '@stripe/react-stripe-js';
import SubscriptionSetupForm from 'components/forms/SubscriptionSetupForm';
import { loadStripe } from '@stripe/stripe-js';
import { processSubscriptionResponse, processLegacySubscriptionPaymentResponse } from 'api/PaymentApiService';
import stripeErrorMessages from 'constants/data/StripeErrors'
import SubscriptionUpgradeForm from 'components/forms/SubscriptionUpgradeForm';
import ChangeSubscriptionResult from 'interface/ChangeSubscriptionResult';

const tag = 'SubscriptionInvoiceConfirmationCard';

function pollForPaymentConfirmation(): Promise<void> {
    return new Promise((resolve) => {
        let attempts = 0;
        const interval = setInterval(() => {
            attempts++;
            void fetchUserSubscription().then((sub) => {
                if (sub?.paymentStatus === 'paid' || attempts >= 8) {
                    clearInterval(interval);
                    resolve();
                }
            }).catch(() => {
                if (attempts >= 8) {
                    clearInterval(interval);
                    resolve();
                }
            });
        }, 2500);
    });
}
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '')

interface SubscriptionInvoiceConfirmationCardProps {
    selectedAccount: AccountType;
    billingCycle: 'monthly' | 'annual';
    beginChangeProcess: boolean;
    setLoading: (loading: boolean) => void;
    onConfirmationFailed: () => void;
    onConfirmationSuccess: () => void;
}

const FAIL_CODES = new Set(['CHANGE_FAILED', 'CANCEL_FAILED', 'CREATE_FAILED', 'NO_SUBSCRIPTION_AND_NO_CREATE', 'USER_NOT_FOUND', 'NO_CUSTOMER', 'NO_TARGET_PRICE', 'PREVIEW_FAILED']);

/**
 * SubscriptionInvoiceConfirmationCard component displays the confirmation details for a subscription invoice.
 *
 * @param {SubscriptionInvoiceConfirmationCardProps} param0 Props for the SubscriptionInvoiceConfirmationCard component.
 * @returns {JSX.Element} The rendered SubscriptionInvoiceConfirmationCard component.
 * 
 * @example
 * Example usage:
 * ```tsx
 * <SubscriptionInvoiceConfirmationCard
 *   selectedAccount={selectedAccount}
 *   billingCycle="monthly"
 *   beginChangeProcess={beginChangeProcess}
 *   setLoading={setLoading}
 *   onConfirmationFailed={handleConfirmationFailed}
 *   onConfirmationSuccess={handleConfirmationSuccess}
 * />
 * ```
 */
const SubscriptionInvoiceConfirmationCard: React.FC<SubscriptionInvoiceConfirmationCardProps> = ({ selectedAccount, billingCycle, beginChangeProcess, setLoading, onConfirmationFailed, onConfirmationSuccess }) => {
    const [confirmationData, setConfirmationData] = useState<SubscriptionChangeResult | null>(null);
    const [setupIntentSecret, setSetupIntentSecret] = useState<string | null>(null);
    const [paymentIntentClientSecret, setPaymentIntentClientSecret] = useState<string | null>(null);
    const { addToast } = useToast();

    /**
     * Create (free -> paid) handler. `SubscriptionSetupForm` only confirms the card (via Stripe's
     * confirmCardSetup) and hands back a payment METHOD id (pm_...) - it never charges anything itself.
     * Activating the subscription - looking up the saved payment method, canceling any stale
     * subscriptions, creating the new one in Stripe - is done server-side by `/subscription/v2/activate`,
     * keyed by the setup intent id alone (the same shared `subscription_setup_intents` row that
     * `startSubscriptionBootstrap` wrote when this "change from free" request created the setup intent).
     */
    const handleCreateAccountPaymentSuccess = useCallback(
        async () => {
            try {
                setLoading(true);
                const setupIntentId = setupIntentSecret ? (setupIntentSecret.split('_secret_')[0] ?? '') : '';
                const result = await activateV2Subscription({ tag: 'SubscriptionInvoiceConfirmationCard', setupIntentId });
                if (!result?.success) {
                    addToast({
                        type: 'error',
                        message: 'Something went wrong activating your subscription. Please contact support.',
                    });
                    return;
                }
                addToast({
                    type: 'success',
                    message:
                        `We're setting up your ${selectedAccount.name} account. It will be active shortly. Thank you for your patience!`,
                });
                await pollForPaymentConfirmation();
            } finally {
                setLoading(false);
                onConfirmationSuccess();
            }
        }, [addToast, onConfirmationSuccess, setLoading, selectedAccount.name, setupIntentSecret]
    );

    const handleCreateAccountPaymentFailure = useCallback(
        async (errorMessage: string) => {
            try {
                setLoading(true);
                await processSubscriptionResponse({ status: 'failed' });
                setLoading(false);

                const defaultError = 'Payment failed. Please try again or contact support if the issue persists.';
                const message = (errorMessage || '').toLowerCase();
                const key = Object.keys(stripeErrorMessages).find(k => message.includes(k.toLowerCase()));
                addToast({
                    type: 'error',
                    message: (key && stripeErrorMessages[key]) || defaultError
                });
            } finally {
                setLoading(false);
                onConfirmationFailed();
            }
        }, [addToast, onConfirmationFailed, setLoading]
    );

    const handleCreateAccountPaymentCancel = useCallback(
        async () => {
            try {
                setLoading(true);
                await processSubscriptionResponse({ status: 'canceled' });
                addToast({
                    type: 'warning',
                    message: 'Payment process was canceled. No changes were made to your subscription.',
                });
            } finally {
                setLoading(false);
            }
            onConfirmationFailed();
        }, [addToast, onConfirmationFailed, setLoading]
    );

    /** Upgrade (paid -> paid) handlers */
    const handleSubscriptionUpgradePayment = useCallback(
        async ({ status, errorMessage, paymentId }: { status: 'succeeded' | 'failed' | 'canceled'; errorMessage?: string; paymentId?: string }) => {
            try {
                setLoading(true);
                if (status === 'succeeded' && paymentId) {
                    await processLegacySubscriptionPaymentResponse({ tag, data: { paymentId, status: 'succeeded', type: 'subscription_update' } });
                    addToast({
                        type: 'success',
                        message: `Your subscription has been updated to ${selectedAccount.name}. Thank you!`,
                    });
                    await pollForPaymentConfirmation();
                    onConfirmationSuccess();
                    return;
                }

                await processLegacySubscriptionPaymentResponse({ tag, data: { status, type: 'subscription_update' } });

                if (errorMessage) {
                    const defaultError = 'Payment failed. Please try again or contact support if the issue persists.';
                    const message = (errorMessage || '').toLowerCase();
                    const key = Object.keys(stripeErrorMessages).find(k => message.includes(k.toLowerCase()));
                    addToast({
                        type: 'error',
                        message: (key && stripeErrorMessages[key]) || defaultError
                    });
                } else if (status === 'canceled') {
                    addToast({
                        type: 'warning',
                        message: 'Payment process was canceled. No changes were made to your subscription.',
                    });
                } else {
                    addToast({
                        type: 'error',
                        message: 'Payment failed. Please try again or contact support if the issue persists.'
                    });
                }
                onConfirmationFailed();
            } finally {
                setLoading(false);
            }
        }, [addToast, onConfirmationFailed, onConfirmationSuccess, setLoading, selectedAccount.name]
    );

    /** Routing helpers */
    const handleAutoPaid = useCallback(() => {
        addToast({
            type: 'success',
            message: `Your subscription has been updated to ${selectedAccount.name} and is being finalized. It will be active shortly. Thank you for your patience!`,
        });
        onConfirmationSuccess();
    }, [addToast, onConfirmationSuccess, selectedAccount.name]);

    const handleCancellationToFree = useCallback(() => {
        addToast({
            type: 'success',
            message: 'You can continue using your service until the end of your billing cycle. We hope to see you again!',
        });
        onConfirmationSuccess();
    }, [addToast, onConfirmationSuccess]);

    const handleCreatedNew = useCallback((data: SubscriptionChangeResult) => {
        const secret = data?.create?.setupIntentSecret;
        if (secret) {
            setSetupIntentSecret(secret);
            addToast({
                type: 'info',
                message: 'Subscription change initialized. Please complete the payment to activate your new plan.',
            });
        }
    }, [addToast]);

    const handleNeedPaymentMethod = useCallback((data: SubscriptionChangeResult) => {
        const secret = (data?.change as ChangeSubscriptionResult)?.paymentIntentClientSecret as string | undefined;
        if (secret) {
            setPaymentIntentClientSecret(secret);
            addToast({
                type: 'info',
                message: 'Payment method required. Please update your payment information.',
            });
        } else {
            // Treat as failure if no payment intent secret is provided
            onConfirmationFailed();
        }
    }, [addToast, onConfirmationFailed, setPaymentIntentClientSecret]);

    const processConfirmationResult = useCallback((data: SubscriptionChangeResult) => {
        if (FAIL_CODES.has(data.code)) {
            onConfirmationFailed();
            return;
        }

        setConfirmationData(data);
        switch(data.code) {
            case 'CANCELLED_TO_FREE':
                handleCancellationToFree();
                break;
            case 'CREATED_NEW':
                handleCreatedNew(data);
                break;
            case 'UPDATED_EXISTING':
                handleAutoPaid();
                break;
            case 'NEED_PAYMENT_METHOD':
                handleNeedPaymentMethod(data);
                break;
            default:
                // Should not reach here due to the FAIL_CODES check above
                onConfirmationFailed();
                break;
        }
    }, [handleCancellationToFree, handleCreatedNew, handleAutoPaid, handleNeedPaymentMethod, onConfirmationFailed]);

    useEffect(() => {
        if (!beginChangeProcess) return;

        const runChangeProcess = async () => {
            setLoading(true);

            try {
                const result = await changeSubscription({
                    tag: 'SubscriptionInvoiceConfirmationCard',
                    data: {
                        accountType: selectedAccount.name,
                        billingCycle
                    }
                });

                if (!result?.success || !result.data?.success) {
                    onConfirmationFailed();
                    return;
                }

                setConfirmationData(result.data);
                processConfirmationResult(result.data)
            } catch {
                setConfirmationData(null);
                onConfirmationFailed();
            } finally {
                setLoading(false);
            }
        }

        void runChangeProcess();
    }, [beginChangeProcess]);

    /* eslint-disable @typescript-eslint/no-misused-promises */
    return (
        <div className={styles.cardContainer}>
            {!stripePromise && (
                <div className={styles.message}>
                    <p>Payment system is not configured. Please contact support.</p>
                </div>
            )}
            {stripePromise && confirmationData && confirmationData.code === 'CREATED_NEW' && setupIntentSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret: setupIntentSecret }} key={`si-${setupIntentSecret}`}>
                    <SubscriptionSetupForm
                        setupIntentSecret={setupIntentSecret}
                        onPaymentSuccess={handleCreateAccountPaymentSuccess}
                        onPaymentError={handleCreateAccountPaymentFailure}
                        onPaymentCancel={handleCreateAccountPaymentCancel}
                    />
                </Elements>
            )}
            {stripePromise && confirmationData && confirmationData.code === 'NEED_PAYMENT_METHOD' && paymentIntentClientSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret: paymentIntentClientSecret }} key={`pi-${paymentIntentClientSecret}`}>
                    <SubscriptionUpgradeForm
                        paymentIntentClientSecret={paymentIntentClientSecret}
                        setLoading={setLoading}
                        handleProcessSubscriptionResponse={handleSubscriptionUpgradePayment}
                    />
                </Elements>
            )}
        </div>
    );
}

export default SubscriptionInvoiceConfirmationCard;